<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { authStore } from '../authStore';
  import { selectedCountry } from '../countryStore';
  import { currentLang, t } from '../i18nStore';

  let isAdmin = false;
  authStore.subscribe(v => {
    isAdmin = v.isAdmin;
  });

  // State Variables
  let campaignStatus = 'stopped';
  let activeTab = 'status'; // 'status' | 'users' | 'adjust' | 'rewards'
  let balances = [];
  let rewards = [];
  let transactions = [];
  
  // Selected user details for transaction history modal
  let selectedUser = null;
  let showHistoryModal = false;

  // Form inputs for points adjustment
  let adjustUserId = '';
  let adjustPoints = 100;
  let adjustAction = 'add'; // 'add' | 'deduct'
  let adjustReason = '';
  let formMessage = '';
  let formError = false;
  let isSubmitting = false;

  // UI state variables
  let isLoadingStatus = true;
  let isLoadingBalances = false;
  let isLoadingRewards = false;
  let isLoadingTransactions = false;

  onMount(async () => {
    await fetchStatus();
  });

  async function fetchStatus() {
    isLoadingStatus = true;
    try {
      const res = await authFetch(getApiUrl('/campaign/status'));
      if (res.ok) {
        const data = await res.json();
        campaignStatus = data.status || 'stopped';
      }
    } catch (e) {
      console.error('Failed to load campaign status', e);
    } finally {
      isLoadingStatus = false;
    }
  }

  async function updateStatus(newStatus) {
    if (!confirm(`Are you sure you want to change the campaign status to ${newStatus}?`)) return;
    try {
      const res = await authFetch(getApiUrl('/admin/campaign/status'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        campaignStatus = newStatus;
        alert(`Campaign is now ${newStatus}`);
      } else {
        alert('Failed to update campaign status');
      }
    } catch (e) {
      console.error(e);
      alert('Error updating status');
    }
  }

  async function fetchBalances() {
    isLoadingBalances = true;
    try {
      const res = await authFetch(getApiUrl('/admin/campaign/balances'));
      if (res.ok) {
        balances = await res.json();
      }
    } catch (e) {
      console.error(e);
    } finally {
      isLoadingBalances = false;
    }
  }

  async function viewUserHistory(user) {
    selectedUser = user;
    isLoadingTransactions = true;
    showHistoryModal = true;
    transactions = [];
    try {
      const res = await authFetch(getApiUrl(`/admin/campaign/transactions/${user.userId}`));
      if (res.ok) {
        transactions = await res.json();
      }
    } catch (e) {
      console.error(e);
    } finally {
      isLoadingTransactions = false;
    }
  }

  async function fetchRewards() {
    isLoadingRewards = true;
    try {
      const countryCode = $selectedCountry || 'HK';
      const res = await authFetch(getApiUrl(`/campaign/rewards?countryCode=${countryCode}`));
      if (res.ok) {
        rewards = await res.json();
      }
    } catch (e) {
      console.error(e);
    } finally {
      isLoadingRewards = false;
    }
  }

  $: if ($selectedCountry && activeTab === 'rewards') {
    fetchRewards();
  }

  async function handleAdjustPoints() {
    if (!adjustUserId || !adjustReason) {
      formError = true;
      formMessage = 'User ID and Reason are required.';
      return;
    }
    
    isSubmitting = true;
    formError = false;
    formMessage = '';

    const ptsAmount = adjustAction === 'add' ? adjustPoints : -adjustPoints;

    try {
      const res = await authFetch(getApiUrl('/admin/points/adjust'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: adjustUserId,
          points: ptsAmount,
          reason: adjustReason
        })
      });

      if (res.ok) {
        formError = false;
        formMessage = `Successfully adjusted points by ${ptsAmount} for user ${adjustUserId}.`;
        // Reset form
        adjustUserId = '';
        adjustReason = '';
      } else {
        const errData = await res.json();
        formError = true;
        formMessage = `Error: ${errData.error || 'Failed to adjust points'}`;
      }
    } catch (e) {
      console.error(e);
      formError = true;
      formMessage = 'Network error: failed to submit adjustment.';
    } finally {
      isSubmitting = false;
    }
  }

  function handleTabChange(tab) {
    activeTab = tab;
    if (tab === 'users') {
      fetchBalances();
    } else if (tab === 'rewards') {
      fetchRewards();
    }
  }
</script>

<div class="admin-campaign-container">
  <div class="glass-header">
    <h2>Campaign 1 Manager: Snapper Points</h2>
    <p class="subtitle">Control promotion rewards, review user points ledger, and manage digital redeems.</p>
  </div>

  <!-- Tab Bar -->
  <div class="tab-bar">
    <button class="tab-btn {activeTab === 'status' ? 'active' : ''}" on:click={() => handleTabChange('status')}>
      Campaign Status
    </button>
    <button class="tab-btn {activeTab === 'users' ? 'active' : ''}" on:click={() => handleTabChange('users')}>
      User Ledgers
    </button>
    <button class="tab-btn {activeTab === 'adjust' ? 'active' : ''}" on:click={() => handleTabChange('adjust')}>
      Points Adjustment
    </button>
    <button class="tab-btn {activeTab === 'rewards' ? 'active' : ''}" on:click={() => handleTabChange('rewards')}>
      Reward Items
    </button>
  </div>

  <!-- Tab Content: Status -->
  {#if activeTab === 'status'}
    <div class="card">
      <h3>Campaign Controller</h3>
      {#if isLoadingStatus}
        <div class="spinner-container"><div class="spinner"></div></div>
      {:else}
        <div class="status-card-inner">
          <div class="status-display">
            <span class="status-indicator-light {campaignStatus}"></span>
            <span class="status-text">Status: <strong>{campaignStatus.toUpperCase()}</strong></span>
          </div>

          <p class="description-text">
            When <strong>ACTIVE</strong>, users will receive 10 points for basic posts and 50 points for rich photo posts.
            When <strong>PAUSED</strong>, the campaign is suspended but data is preserved.
            When <strong>STOPPED</strong>, the points program is offline.
          </p>

          <div class="action-btn-group">
            <button class="btn success" disabled={campaignStatus === 'active'} on:click={() => updateStatus('active')}>
              Start Campaign
            </button>
            <button class="btn warning" disabled={campaignStatus === 'paused'} on:click={() => updateStatus('paused')}>
              Pause Campaign
            </button>
            <button class="btn danger" disabled={campaignStatus === 'stopped'} on:click={() => updateStatus('stopped')}>
              Stop Campaign
            </button>
          </div>
        </div>
      {/if}
    </div>
  {/if}

  <!-- Tab Content: Users List -->
  {#if activeTab === 'users'}
    <div class="card">
      <div class="card-header-row">
        <h3>User Points Ledgers</h3>
        <button class="sm-btn refresh-btn" on:click={fetchBalances}>Refresh</button>
      </div>

      {#if isLoadingBalances}
        <div class="spinner-container"><div class="spinner"></div></div>
      {:else if balances.length === 0}
        <p class="empty-text">No points transactions recorded yet.</p>
      {:else}
        <div class="table-wrapper">
          <table class="premium-table">
            <thead>
              <tr>
                <th>User ID</th>
                <th>Current Balance</th>
                <th>Total Earned</th>
                <th>Last Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {#each balances as bal}
                <tr>
                  <td class="font-mono">{bal.userId}</td>
                  <td class="font-bold font-color-primary">{bal.balance.toLocaleString()}</td>
                  <td class="font-color-success">{bal.totalEarned.toLocaleString()}</td>
                  <td>{new Date(bal.lastUpdated).toLocaleString()}</td>
                  <td>
                    <button class="action-table-btn" on:click={() => viewUserHistory(bal)}>View History</button>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </div>
  {/if}

  <!-- Tab Content: Manual Adjust -->
  {#if activeTab === 'adjust'}
    <div class="card max-w-md">
      <h3>Points Adjustment Form</h3>
      <p class="description-text">Directly credit or debit a user's Snapper Points. All adjustments are logged for audit.</p>

      <form on:submit|preventDefault={handleAdjustPoints} class="premium-form">
        <div class="form-group">
          <label for="userId">Target User ID</label>
          <input type="text" id="userId" bind:value={adjustUserId} placeholder="e.g. auth0|12345" required />
        </div>

        <div class="form-row-double">
          <div class="form-group">
            <label for="action">Action</label>
            <select id="action" bind:value={adjustAction}>
              <option value="add">Add Points (+)</option>
              <option value="deduct">Deduct Points (-)</option>
            </select>
          </div>
          <div class="form-group">
            <label for="points">Amount</label>
            <input type="number" id="points" bind:value={adjustPoints} min="1" max="10000" required />
          </div>
        </div>

        <div class="form-group">
          <label for="reason">Audit Reason</label>
          <input type="text" id="reason" bind:value={adjustReason} placeholder="e.g. Contributor rewards correction" required />
        </div>

        {#if formMessage}
          <div class="form-alert {formError ? 'error' : 'success'}">
            {formMessage}
          </div>
        {/if}

        <button type="submit" class="submit-btn" disabled={isSubmitting}>
          {isSubmitting ? 'Adjusting...' : 'Submit Points Adjustment'}
        </button>
      </form>
    </div>
  {/if}

  <!-- Tab Content: Reward Catalog -->
  {#if activeTab === 'rewards'}
    <div class="card">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem; flex-wrap: wrap; gap: 1rem;">
        <div>
          <h3 style="margin: 0 0 0.25rem 0;">E-Vouchers & Rewards Catalogue</h3>
          <p class="description-text" style="margin: 0;">Catalogue of redeemable electronic vouchers for Snapper Points.</p>
        </div>
        <a href="/admin/rewards" class="btn" style="background-color: #6366f1; color: white; text-decoration: none; padding: 0.5rem 1rem; border-radius: 6px; font-weight: 600; font-size: 0.85rem; display: inline-block;">Manage Rewards Catalog</a>
      </div>

      {#if isLoadingRewards}
        <div class="spinner-container"><div class="spinner"></div></div>
      {:else if rewards.length === 0}
        <p class="empty-text">Catalogue empty.</p>
      {:else}
        <div class="rewards-grid">
          {#each rewards as reward}
            <div class="reward-item-card">
              <div class="reward-badge {reward.quantity === 0 ? 'out-of-stock-badge' : ''}">
                {reward.quantity === 0 ? $t('rewards.out_of_stock') : 'Voucher'}
              </div>
              <h4>{reward.name[$currentLang] || reward.name['en'] || ''}</h4>
              <p>{reward.description[$currentLang] || reward.description['en'] || ''}</p>
              <div class="price-tag">{reward.pointsRequired.toLocaleString()} Points</div>
              {#if isAdmin}
                <div class="qty-info">Quantity: {reward.quantity}</div>
              {/if}
            </div>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</div>

<!-- Transaction History Overlay Modal -->
{#if showHistoryModal}
  <div class="modal-backdrop" on:click={() => showHistoryModal = false}>
    <div class="modal-content" on:click|stopPropagation>
      <div class="modal-header">
        <h4>Points Transaction History</h4>
        <button class="close-modal-btn" on:click={() => showHistoryModal = false}>&times;</button>
      </div>
      <div class="modal-body">
        <p class="user-info">User: <strong>{selectedUser?.userId}</strong></p>
        
        {#if isLoadingTransactions}
          <div class="spinner-container"><div class="spinner"></div></div>
        {:else if transactions.length === 0}
          <p class="empty-text">No points transactions found for this user.</p>
        {:else}
          <div class="modal-table-wrapper">
            <table class="modal-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Points</th>
                  <th>Reason</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {#each transactions as tx}
                  <tr>
                    <td>
                      <span class="tx-badge {tx.type}">
                        {tx.type.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td class="font-bold {tx.points > 0 ? 'font-color-success' : 'font-color-danger'}">
                      {tx.points > 0 ? '+' : ''}{tx.points.toLocaleString()}
                    </td>
                    <td>{tx.reason}</td>
                    <td>{new Date(tx.createdAt).toLocaleString()}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .admin-campaign-container {
    max-width: 1000px;
    margin: 2rem auto;
    padding: 0 1rem;
    font-family: 'Inter', -apple-system, sans-serif;
  }

  .glass-header {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 2rem;
    border-left: 4px solid #3b82f6;
  }

  .glass-header h2 {
    margin: 0;
    font-size: 1.8rem;
    color: #1e293b;
    font-weight: 700;
  }

  .subtitle {
    margin: 0.5rem 0 0 0;
    color: #64748b;
    font-size: 0.95rem;
  }

  .tab-bar {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 0.5rem;
    overflow-x: auto;
  }

  .tab-btn {
    background: none;
    border: none;
    padding: 0.6rem 1.2rem;
    color: #64748b;
    font-weight: 600;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.2s;
    font-size: 0.9rem;
    white-space: nowrap;
  }

  .tab-btn:hover {
    color: #1e293b;
    background: #f1f5f9;
  }

  .tab-btn.active {
    color: #3b82f6;
    background: #eff6ff;
  }

  .card {
    background: white;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    padding: 2rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    margin-bottom: 2rem;
  }

  .max-w-md {
    max-width: 550px;
  }

  .card h3 {
    margin-top: 0;
    margin-bottom: 1rem;
    font-size: 1.25rem;
    color: #1e293b;
  }

  .card-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .card-header-row h3 {
    margin: 0;
  }

  .status-card-inner {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .status-display {
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  .status-indicator-light {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    display: inline-block;
  }

  .status-indicator-light.active {
    background-color: #10b981;
    box-shadow: 0 0 8px #10b981;
  }

  .status-indicator-light.paused {
    background-color: #f59e0b;
    box-shadow: 0 0 8px #f59e0b;
  }

  .status-indicator-light.stopped {
    background-color: #ef4444;
    box-shadow: 0 0 8px #ef4444;
  }

  .status-text {
    font-size: 1.1rem;
    color: #334155;
  }

  .description-text {
    color: #64748b;
    font-size: 0.9rem;
    line-height: 1.5;
    margin: 0;
  }

  .action-btn-group {
    display: flex;
    gap: 0.8rem;
  }

  .btn {
    border: none;
    padding: 0.7rem 1.4rem;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 0.9rem;
  }

  .btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .btn.success {
    background-color: #10b981;
    color: white;
  }
  .btn.success:hover:not(:disabled) {
    background-color: #059669;
  }

  .btn.warning {
    background-color: #f59e0b;
    color: white;
  }
  .btn.warning:hover:not(:disabled) {
    background-color: #d97706;
  }

  .btn.danger {
    background-color: #ef4444;
    color: white;
  }
  .btn.danger:hover:not(:disabled) {
    background-color: #dc2626;
  }

  .sm-btn {
    border: none;
    padding: 0.4rem 0.8rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
  }

  .refresh-btn {
    background-color: #f1f5f9;
    color: #475569;
  }
  .refresh-btn:hover {
    background-color: #e2e8f0;
  }

  .empty-text {
    color: #94a3b8;
    text-align: center;
    padding: 2rem 0;
    margin: 0;
  }

  /* Table styling */
  .table-wrapper {
    overflow-x: auto;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
  }

  .premium-table {
    width: 100%;
    border-collapse: collapse;
    text-align: left;
    font-size: 0.9rem;
  }

  .premium-table th {
    background-color: #f8fafc;
    padding: 0.8rem 1rem;
    color: #475569;
    font-weight: 600;
    border-bottom: 1px solid #e2e8f0;
  }

  .premium-table td {
    padding: 0.8rem 1rem;
    border-bottom: 1px solid #e2e8f0;
    color: #334155;
  }

  .premium-table tr:hover {
    background-color: #f8fafc;
  }

  .font-mono {
    font-family: monospace;
    font-size: 0.85rem;
  }

  .font-bold {
    font-weight: 700;
  }

  .font-color-primary {
    color: #3b82f6;
  }

  .font-color-success {
    color: #10b981;
  }

  .font-color-danger {
    color: #ef4444;
  }

  .action-table-btn {
    background: none;
    border: none;
    color: #3b82f6;
    font-weight: 600;
    cursor: pointer;
    font-size: 0.85rem;
  }
  .action-table-btn:hover {
    text-decoration: underline;
  }

  /* Form Styles */
  .premium-form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    margin-top: 1.5rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .form-group label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #475569;
  }

  .form-group input, .form-group select {
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    padding: 0.6rem;
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.2s;
  }

  .form-group input:focus, .form-group select:focus {
    border-color: #3b82f6;
  }

  .form-row-double {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .submit-btn {
    border: none;
    background-color: #3b82f6;
    color: white;
    padding: 0.7rem;
    border-radius: 6px;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .submit-btn:hover:not(:disabled) {
    background-color: #2563eb;
  }

  .submit-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .form-alert {
    padding: 0.8rem;
    border-radius: 6px;
    font-size: 0.85rem;
    font-weight: 500;
  }

  .form-alert.error {
    background-color: #fef2f2;
    color: #b91c1c;
    border: 1px solid #fecaca;
  }

  .form-alert.success {
    background-color: #f0fdf4;
    color: #15803d;
    border: 1px solid #bbf7d0;
  }

  /* Catalogue Grid */
  .rewards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-top: 1.5rem;
  }

  .reward-item-card {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.5rem;
    background-color: #f8fafc;
    position: relative;
  }

  .reward-badge {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background-color: #eff6ff;
    color: #3b82f6;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.2rem 0.5rem;
    border-radius: 12px;
  }

  .reward-badge.out-of-stock-badge {
    background-color: #fee2e2;
    color: #ef4444;
  }

  .qty-info {
    margin-top: 0.6rem;
    font-size: 0.8rem;
    color: #64748b;
    font-weight: 600;
  }

  .reward-item-card h4 {
    margin-top: 0;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    color: #1e293b;
  }

  .reward-item-card p {
    color: #64748b;
    font-size: 0.85rem;
    margin-top: 0;
    margin-bottom: 1.5rem;
    line-height: 1.4;
  }

  .price-tag {
    font-weight: 700;
    color: #0f172a;
    font-size: 1rem;
  }

  /* Spinner styles */
  .spinner-container {
    display: flex;
    justify-content: center;
    padding: 2rem 0;
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e2e8f0;
    border-top: 3px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* Modal overlay styles */
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }

  .modal-content {
    background-color: white;
    width: 90%;
    max-width: 650px;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }

  .modal-header {
    background-color: #f8fafc;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal-header h4 {
    margin: 0;
    color: #1e293b;
    font-size: 1.15rem;
  }

  .close-modal-btn {
    background: none;
    border: none;
    font-size: 1.8rem;
    line-height: 1;
    cursor: pointer;
    color: #94a3b8;
  }
  .close-modal-btn:hover {
    color: #475569;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .user-info {
    font-size: 0.9rem;
    color: #475569;
    margin-top: 0;
    margin-bottom: 1rem;
  }

  .modal-table-wrapper {
    max-height: 350px;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
  }

  .modal-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;
  }

  .modal-table th {
    background-color: #f8fafc;
    padding: 0.6rem 0.8rem;
    font-weight: 600;
    color: #475569;
    border-bottom: 1px solid #e2e8f0;
  }

  .modal-table td {
    padding: 0.6rem 0.8rem;
    border-bottom: 1px solid #e2e8f0;
    color: #334155;
  }

  .tx-badge {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.1rem 0.4rem;
    border-radius: 4px;
    display: inline-block;
  }

  .tx-badge.promo_post {
    background-color: #f0fdf4;
    color: #166534;
  }
  .tx-badge.manual_adjust {
    background-color: #fef3c7;
    color: #92400e;
  }
  .tx-badge.redeem {
    background-color: #eff6ff;
    color: #1e40af;
  }
</style>
